import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getNewArrivalItems,
  getTopSellingItems,
} from "@/api/take-order/take-order";

const TOP_SELLING_CACHE_KEY = "takeOrderTopSellingItemsCache";
const NEW_ARRIVAL_CACHE_KEY = "takeOrderNewArrivalItemsCache";

const readCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

const writeCache = (key, payload) => {
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch (error) {
    // Ignore storage errors (e.g., private mode).
  }
};

const getTodayKey = () => new Date().toISOString().slice(0, 10);

export const clearNewArrivalItemsCache = () => {
  try {
    localStorage.removeItem(NEW_ARRIVAL_CACHE_KEY);
  } catch (error) {
    // Ignore storage errors.
  }
};

export const fetchTopSellingItems = createAsyncThunk(
  "takeOrderHomePage/fetchTopSellingItems",
  async ({ restaurant_id, force = false }, { rejectWithValue }) => {
    const today = getTodayKey();
    const cached = readCache(TOP_SELLING_CACHE_KEY);

    if (!force && cached?.date === today && Array.isArray(cached?.items)) {
      return cached.items;
    }

    const res = await getTopSellingItems(restaurant_id);
    if (res?.status === "success") {
      writeCache(TOP_SELLING_CACHE_KEY, { date: today, items: res.data?.items || [] });
      return res.data?.items || [];
    }

    return rejectWithValue(res?.message || "Failed to fetch top selling items.");
  }
);

export const fetchNewArrivalItems = createAsyncThunk(
  "takeOrderHomePage/fetchNewArrivalItems",
  async ({ restaurant_id, force = false }, { rejectWithValue }) => {
    const today = getTodayKey();
    const cached = readCache(NEW_ARRIVAL_CACHE_KEY);

    if (!force && cached?.date === today && Array.isArray(cached?.items)) {
      return cached.items;
    }

    const res = await getNewArrivalItems(restaurant_id);
    if (res?.status === "success") {
      writeCache(NEW_ARRIVAL_CACHE_KEY, { date: today, items: res.data?.items || [] });
      return res.data?.items || [];
    }

    return rejectWithValue(res?.message || "Failed to fetch new arrival items.");
  }
);

const initialState = {
  topSellingItems: [],
  newArrivalItems: [],
  loading: {
    topSelling: false,
    newArrival: false,
  },
  error: {
    topSelling: null,
    newArrival: null,
  },
};

const homePageItemsSlice = createSlice({
  name: "takeOrderHomePage",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTopSellingItems.pending, (state) => {
        state.loading.topSelling = true;
        state.error.topSelling = null;
      })
      .addCase(fetchTopSellingItems.fulfilled, (state, action) => {
        state.loading.topSelling = false;
        state.topSellingItems = action.payload;
      })
      .addCase(fetchTopSellingItems.rejected, (state, action) => {
        state.loading.topSelling = false;
        state.error.topSelling = action.payload;
      })
      .addCase(fetchNewArrivalItems.pending, (state) => {
        state.loading.newArrival = true;
        state.error.newArrival = null;
      })
      .addCase(fetchNewArrivalItems.fulfilled, (state, action) => {
        state.loading.newArrival = false;
        state.newArrivalItems = action.payload;
      })
      .addCase(fetchNewArrivalItems.rejected, (state, action) => {
        state.loading.newArrival = false;
        state.error.newArrival = action.payload;
      });
  },
});

export default homePageItemsSlice.reducer;
